import { createStyles, makeStyles } from '@material-ui/styles';
import * as React from 'react';
import { FC, useState, useEffect, useReducer } from 'react';
import Image from './Image';
import mediaQueries from 'src/constants/responsive';
import spacing from 'src/constants/spacing';
import { Link } from 'react-router-dom';
import stylesheet, { tertiaryColor, secondaryColor, mainColor } from 'src/constants/stylesheet';
import { useApi } from 'src/@context/Api';
import * as classNames from 'classnames';
import { Responsive } from 'src/@context/Responsive';

const gridGutter = 15;

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      overflow: 'hidden',
    },
    projectCategories: {
      position: 'relative',
      maxWidth: spacing.websiteMaxWidth,
      margin: '0 auto',
      [mediaQueries.Mobile]: {
        display: 'none',
      },
      [mediaQueries.Tablet]: {
        paddingTop: spacing.Gutter.Tablet / 2,
        paddingRight: spacing.Gutter.Tablet,
        paddingLeft: spacing.Gutter.Tablet,
      },
      [mediaQueries.Laptop]: {
        paddingTop: spacing.Gutter.Laptop / 2,
        paddingRight: spacing.Gutter.Laptop,
        paddingLeft: spacing.Gutter.Laptop,
      },
      [mediaQueries.Desktop]: {
        paddingTop: spacing.Gutter.Desktop / 2,
        paddingRight: spacing.Gutter.Desktop,
        paddingLeft: spacing.Gutter.Desktop,
      },

      '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        height: 1,
        width: '100vw',
        left: '50%',
        backgroundColor: tertiaryColor,
        transform: 'translateX(-50%)'
      },

      '&:after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        height: 1,
        width: '100vw',
        left: '50%',
        backgroundColor: tertiaryColor,
        transform: 'translateX(-50%)'
      }
    },
    projectCategoriesInner: {
      [mediaQueries.Mobile]: {
        padding: '10px 0'
      },
      [mediaQueries.AfterMobile]: {
        marginLeft: -10,
        marginRight: -10,
      },
    },
    category: {
      display: 'inline-block',
      margin: '0 10px 20px 10px',
      color: tertiaryColor,
      ...stylesheet.Light,
      fontSize: 14,
      textTransform: 'uppercase',
      cursor: 'pointer',

      '&:hover': {
        color: mainColor
      },

      '&.active': {
        ...stylesheet.MediumText,
        color: secondaryColor
      }
    },
    projectContent: {
      position: 'relative',
      maxWidth: spacing.websiteMaxWidth,
      margin: '0 auto',
      [mediaQueries.Mobile]: {
        paddingTop: spacing.Gutter.Mobile,
        paddingRight: spacing.Gutter.Mobile,
        paddingLeft: spacing.Gutter.Mobile,
        paddingBottom: spacing.Gutter.Mobile * 2,
      },
      [mediaQueries.Tablet]: {
        paddingTop: spacing.Gutter.Tablet / 2,
        paddingBottom: spacing.Gutter.Tablet,
        paddingRight: spacing.Gutter.Tablet,
        paddingLeft: spacing.Gutter.Tablet,
      },
      [mediaQueries.Laptop]: {
        paddingTop: spacing.Gutter.Laptop / 2,
        paddingBottom: spacing.Gutter.Laptop,
        paddingRight: spacing.Gutter.Laptop,
        paddingLeft: spacing.Gutter.Laptop,
      },
      [mediaQueries.Desktop]: {
        paddingTop: spacing.Gutter.Desktop / 2,
        paddingBottom: spacing.Gutter.Desktop,
        paddingRight: spacing.Gutter.Desktop,
        paddingLeft: spacing.Gutter.Desktop,
      },
    },
    hide: {},
    projectContentInner: {
      display: 'flex',
      flexWrap: 'wrap',
      opacaty: 1,
      transition: 'opacity .4s ease',
      [mediaQueries.Mobile]: {},
      [mediaQueries.Tablet]: {
        marginLeft: -gridGutter / 2,
        marginRight: -gridGutter / 2,
      },
      [mediaQueries.Laptop]: {
        marginLeft: -gridGutter / 2,
        marginRight: -gridGutter / 2,
      },
      [mediaQueries.Desktop]: {
        marginLeft: -gridGutter / 2,
        marginRight: -gridGutter / 2,
      },

      '&$hide': {
        opacity: 0,
        height: 0
      }
    },
    project: {
      [mediaQueries.Mobile]: {
        flex: `100%`,
      },
      [mediaQueries.AfterMobile]: {
        marginBottom: gridGutter,
      },
      [mediaQueries.Tablet]: {
        flex: `0 0 ${1 / 3 * 100}%`,
        width: `${1 / 3 * 100}%`,
        paddingLeft: gridGutter / 2,
        paddingRight: gridGutter / 2,
      },
      [mediaQueries.Laptop]: {
        flex: `0 0 ${1 / 3 * 100}%`,
        width: `${1 / 3 * 100}%`,
        paddingLeft: gridGutter / 2,
        paddingRight: gridGutter / 2,
      },
      [mediaQueries.Desktop]: {
        flex: `0 0 ${1 / 3 * 100}%`,
        width: `${1 / 3 * 100}%`,
        paddingLeft: gridGutter / 2,
        paddingRight: gridGutter / 2,
      },
    },
    projectPreviewImage: {
      position: 'relative',
      height: 0,
      paddingBottom: '63.29%'
    },
    projectPreviewImageInner: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    },
    hoverText: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      opacity: 0,
      color: 'white',
      transition: 'opacity .4s ease',
      pointerEvents: 'none',
      zIndex: 1,

      '$projectPreviewImage:hover &': {
        opacity: 1
      }
    },
    projectLink: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1,

      '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(12, 0, 176, 0.8)',
        opacity: 0,
        transition: 'opacity .4s ease',
      },

      '&:hover:before': {
        opacity: 1
      }
    },
    imagePreview: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      ...stylesheet.ObjectFitCover
    },
    projectTitle: {
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      lineHeight: '52px',
      textAlign: 'center',
    },
    label: {
      display: 'block',
      '& span': {
        display: 'block',
        textAlign: 'center',
        marginBottom: 5
      }
    },
    select: {
      display: 'block',
      margin: '0 auto',
      fontSize: '1.2rem',
      fontFamily: 'inherit',
      padding: 0
    },
    selectOption: {
    },
    // seeMoreBtn: {
    //   display: 'block',
    //   margin: '0 auto',
    //   border: 0,
    //   fontSize: '1rem',
    //   cursor: 'pointer',
    //   color: 'white',
    //   backgroundColor: secondaryColor,
    //   textTransform: 'uppercase',
    //   padding: '4px 30px',
    //   '&:hover': {
    //     backgroundColor: tertiaryColor,
    //   },
    //   [mediaQueries.Mobile]: {
    //     margin: '40px auto 0 auto',
    //   },
    // },
    active: {
      color: secondaryColor
    },
    show: {},
    fetching: {
      fontSize: '2rem',
      color: secondaryColor,
      textAlign: 'center',
      opacity: 0,
      transition: 'opacity .4s ease',

      '&$show': {
        opacity: 1,
      }
    }
  }));
export type ProjectsProps = ProjectsDto;

type Props = ProjectsProps;

const Projects: FC<Props> = ({ UID }) => {
  const cls = useStyles();
  const projectsToDisplay = 12;
  const { app, fetchProjects } = useApi();
  const projectCategories = app && app.Config.ProjectCategories;
  const [category, setCategory] = useState<string>('');
  const [projects, setProjects] = useState<ProjectPreviewDto[]>([]);
  const [fetching, setFetching] = useState(false);
  const [fetchingCategory, setFetchingCategory] = useState(false);

  const sizes = '(max-width: 689px) 100vw, (min-width: 690px and max-width: 1023px) calc(100vw / 3), 350px'

  const countReducer = (state: number, action: string) => {
    switch (action) {
      case "INCREASE":
        return state += projectsToDisplay;
      case "RESET":
        return 0;
      default:
        return state;
    }
  }

  const allProjectLoadedReducer = (state: boolean, action: string) => {
    switch (action) {
      case "scroll":
        const modulo = projects.length % projectsToDisplay;
        if (modulo < projectsToDisplay && modulo !== 0) {
          return true;
        } else {
          return false;
        }
      case "category":
        return false;
      default:
        return false;
    }
  }

  const [count, dispath] = useReducer(countReducer, 0);
  const [allLoaded, dispathLoaded] = useReducer(allProjectLoadedReducer, false);

  const loadMore = () => {
    if (allLoaded) { return; }

    setFetching(true);
    fetchProjects(count, projectsToDisplay, `query=${category}`)
      .then(p => {
        if (count > 0) {
          setProjects([...projects, ...p]);
        } else {
          setProjects([...p]);
        }
        setFetching(false);
        setFetchingCategory(false)
      })
      // .then(()=>  dispathLoaded('scroll'));
  };

  useEffect(() => {
    loadMore();
  }, [category, count]);

  useEffect(() => {
    const scrollingontainer = document.scrollingElement;
    if (!scrollingontainer) { return }

    const onScroll = () => {
      if (!scrollingontainer || fetching) { return; }
      // check if the size is in bottom, 
      if ((scrollingontainer.scrollTop + scrollingontainer.clientHeight) < scrollingontainer.scrollHeight) {
        return;
      }

      dispathLoaded('scroll');
      dispath("INCREASE");
    };

    window.addEventListener("scroll", onScroll);

    return (() => window.removeEventListener('scroll', onScroll));

  }, []);

  return (
    <section className={cls.root}>
      {projectCategories &&
        <div className={cls.projectCategories}>
          <div className={cls.projectCategoriesInner}>
            <Responsive steps={["Mobile"]}>
              <label className={cls.label}>
                <span>Category</span>
                <select
                  className={cls.select}
                  name="categories"
                  id="categories"
                  // tslint:disable-next-line:jsx-no-lambda
                  onChange={e => setCategory(e.target.value)}
                >
                  <option value="">TOUS</option>
                  {projectCategories.map(c =>
                    <option
                      className={cls.selectOption}
                      key={c.Value}
                      value={c.Value}
                    >
                      {c.Key}
                    </option>
                  )}
                </select>
              </label>
            </Responsive>
            <Responsive steps={["AfterMobile"]}>
              <span
                key={"all"}
                className={classNames(cls.category, !category && cls.active)}
                // tslint:disable-next-line:jsx-no-lambda
                onClick={() => {
                  dispath("RESET");
                  dispathLoaded("category");
                  setFetchingCategory(true);
                  setCategory('');
                }}
              >
                TOUS
              </span>
              {projectCategories.map(c =>
                <span
                  key={c.Value}
                  className={classNames(cls.category, category === c.Value && cls.active)}
                  // tslint:disable-next-line:jsx-no-lambda
                  onClick={() => {
                    dispath("RESET");
                    dispathLoaded("category");
                    setFetchingCategory(true);
                    setCategory(c.Value);
                  }}
                >
                  {c.Key}
                </span>
              )}
            </Responsive>
          </div>
        </div>
      }
      <div className={cls.projectContent}>
        <div className={classNames(cls.projectContentInner, fetchingCategory && cls.hide)}>
          {
            projects && projects.map(p =>
              <article
                key={`project-${p.UID}`}
                className={cls.project}
              >
                <div className={cls.projectPreviewImage}>
                  <div className={cls.projectPreviewImageInner}>
                    <Link
                      to={`/${UID}/${p.UID}`}
                      className={cls.projectLink}
                    />
                    {p.HoverText &&
                      <div className={cls.hoverText}>{p.HoverText}</div>
                    }
                    <Image
                      pictureRef={p.ImagePreview}
                      className={cls.imagePreview}
                      sizes={sizes}
                    />
                  </div>
                </div>
                <h6 className={cls.projectTitle}>{p.Title}</h6>
              </article>
            )
          }
        </div>
        <div className={classNames(cls.fetching, fetching && cls.show)}>Loading</div>
      </div>
    </section>
  );
};

export default Projects;


// WEBPACK FOOTER //
// ./src/Components/Projects.tsx